import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock3, ChevronRight, Eye, Heart, MessageSquare, Send, UserRound, Share2, Twitter, Linkedin, Facebook, Link as LinkIcon, Check } from 'lucide-react';
import BlogCard from './BlogCard';
import CommentList from './CommentList';
import ExperienceBanner from './ExperienceBanner';
import { useAuth } from '../contexts/AuthContext';
import BlogNavbar from './BlogNavbar';
import BlogDetailSkeleton from './skeletons/BlogDetailSkeleton';
import {
  fetchBlogComments,
  fetchBlogDetail,
  fetchMyLikeStatus,
  formatViewsLabel,
  postBlogComment,
  syncBlogDetailMetricsCache,
  toggleBlogLike,
  trackBlogView
} from '../services/blogService';
import { applySeo } from '../utils/seo';

const BlogShareCard = ({ blog }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/blogs/${blog?.slug || ''}`;
  const shareText = `${blog?.title || 'Check out this blog'} — Race Next Step`;

  const shareLinks = [
    {
      key: 'twitter',
      label: 'Twitter / X',
      Icon: Twitter,
      color: 'bg-[#e7f3ff] text-[#1d9bf0] hover:bg-[#1d9bf0] hover:text-white',
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      Icon: Linkedin,
      color: 'bg-[#e8f0fb] text-[#0a66c2] hover:bg-[#0a66c2] hover:text-white',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    },
    {
      key: 'facebook',
      label: 'Facebook',
      Icon: Facebook,
      color: 'bg-[#edf2ff] text-[#1877f2] hover:bg-[#1877f2] hover:text-white',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    }
  ];

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: blog?.title || 'Blog', text: shareText, url: shareUrl });
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="mt-6 rounded-[30px] border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
      <div className="mb-4 flex items-center gap-3 text-[#2a1f38]">
        <Share2 className="h-5 w-5 text-[#a43ddd]" />
        <h3 className="text-xl font-bold">Share This Blog</h3>
      </div>
      <p className="mb-5 text-sm leading-6 text-[#685d74]">Found this helpful? Share it with others!</p>

      <div className="space-y-3">
        {shareLinks.map(({ key, label, Icon, color, href }) => (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold transition-all duration-200 ${color}`}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {label}
          </a>
        ))}
        {'share' in navigator && (
          <button
            type="button"
            onClick={handleNativeShare}
            className="flex w-full items-center gap-3 rounded-2xl bg-gradient-to-r from-[#9a51a0]/10 to-[#f36f21]/10 px-4 py-3 text-sm font-semibold text-[#7f3df1] transition-all duration-200 hover:from-[#9a51a0]/20 hover:to-[#f36f21]/20"
          >
            <Share2 className="h-4 w-4 shrink-0" />
            More Options
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={handleCopyLink}
        className={`mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition-all duration-200 ${
          copied
            ? 'border-green-200 bg-green-50 text-green-600'
            : 'border-[#eadcf8] bg-gray-50 text-[#685d74] hover:border-[#b86ae6] hover:text-[#7f3df1]'
        }`}
      >
        {copied ? (
          <><Check className="h-4 w-4" /> Link Copied!</>
        ) : (
          <><LinkIcon className="h-4 w-4" /> Copy Link</>
        )}
      </button>
    </div>
  );
};

const ShareInlineButton = ({ blog, fullWidth = false }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);
  const shareUrl = `${window.location.origin}/blogs/${blog?.slug || ''}`;
  const shareText = `${blog?.title || 'Check out this blog'} — Race Next Step`;

  useEffect(() => {
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => { setCopied(false); setOpen(false); }, 2000);
    });
  };

  const shareOptions = [
    {
      key: 'twitter',
      label: 'Twitter / X',
      Icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      Icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    },
    {
      key: 'facebook',
      label: 'Facebook',
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    }
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex h-full w-full items-center justify-center gap-1.5 rounded-xl border px-2 py-2.5 text-xs font-semibold transition-all ${
          open
            ? 'border-[#d7b3f4] bg-[#f4ecff] text-[#7f3df1]'
            : fullWidth
              ? 'border-[#eadffb] bg-white text-[#6b5a82] hover:bg-[#f8f3ff]'
              : 'bg-[#f4ecff] text-[#9a51a0] hover:bg-[#ede0ff]'
        }`}
      >
        <Share2 className="h-3.5 w-3.5 shrink-0" />
        {fullWidth && <span className="hidden sm:inline">Share</span>}
      </button>

      {open && (
        <div className="absolute bottom-[calc(100%+6px)] left-1/2 z-50 w-44 -translate-x-1/2 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.14)]">
          <div className="bg-gradient-to-r from-[#9a51a0] to-[#f36f21] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white">
            Share this blog
          </div>
          <div className="space-y-1 p-2">
            {shareOptions.map(({ key, label, Icon, href }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-gray-50"
              >
                <Icon className="h-4 w-4 shrink-0 text-[#9a51a0]" />
                {label}
              </a>
            ))}
            {'share' in navigator && (
              <button
                type="button"
                onClick={() => { navigator.share({ title: blog?.title, text: shareText, url: shareUrl }); setOpen(false); }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-gray-50"
              >
                <Share2 className="h-4 w-4 shrink-0 text-[#9a51a0]" />
                More Options
              </button>
            )}
            <button
              type="button"
              onClick={handleCopy}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
                copied ? 'bg-green-50 text-green-600' : 'text-slate-700 hover:bg-gray-50'
              }`}
            >
              {copied ? <Check className="h-4 w-4 shrink-0" /> : <LinkIcon className="h-4 w-4 shrink-0 text-[#9a51a0]" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [blog, setBlog] = useState(null);
  const [commentList, setCommentList] = useState([]);
  const [metrics, setMetrics] = useState({ likeCount: 0, commentCount: 0, viewCount: 0 });
  const [hasLiked, setHasLiked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [likeLoading, setLikeLoading] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [engagementMessage, setEngagementMessage] = useState('');
  const likeMutationIdRef = useRef(0);
  const likeStatusRequestIdRef = useRef(0);
  const sanitizeMetricValue = (value) => Math.max(0, Number(value || 0));

  const formattedComments = useMemo(
    () =>
      commentList.map((comment) => ({
        ...comment,
        formattedDate: new Date(comment.createdAt).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        })
      })),
    [commentList]
  );
  const displayLikeCount = useMemo(() => sanitizeMetricValue(metrics.likeCount), [metrics.likeCount]);
  const currentViewsLabel = useMemo(() => formatViewsLabel(metrics.viewCount || 0), [metrics.viewCount]);
  const displayCommentCount = useMemo(
    () => Math.max(sanitizeMetricValue(metrics.commentCount), commentList.length),
    [metrics.commentCount, commentList.length]
  );
  const applyMetrics = (nextMetrics) => {
    if (!nextMetrics) {
      return;
    }

    setMetrics((current) => {
      const mergedMetrics = {
        ...current,
        ...nextMetrics,
        likeCount: sanitizeMetricValue(nextMetrics.likeCount ?? current.likeCount),
        commentCount: sanitizeMetricValue(nextMetrics.commentCount ?? current.commentCount),
        viewCount: sanitizeMetricValue(nextMetrics.viewCount ?? current.viewCount)
      };
      syncBlogDetailMetricsCache(slug, mergedMetrics);
      return mergedMetrics;
    });
  };

  const refreshMetricsFromServer = async () => {
    try {
      const freshBlogData = await fetchBlogDetail(slug, { fresh: true });
      applyMetrics(freshBlogData.metrics || {});
    } catch (_) {}
  };

  useEffect(() => {
    let isMounted = true;

    const loadBlog = async () => {
      try {
        setLoading(true);
        setCommentsLoading(true);
        setNotFound(false);
        setError('');
        setEngagementMessage('');
        const blogData = await fetchBlogDetail(slug);

        if (!isMounted) {
          return;
        }

        setBlog(blogData);
        setMetrics(blogData.metrics || { likeCount: 0, commentCount: 0, viewCount: 0 });
      } catch (fetchError) {
        if (!isMounted) {
          return;
        }

        if (String(fetchError.message || '').toLowerCase().includes('not found')) {
          setNotFound(true);
        } else {
          setError(fetchError.message || 'Failed to load blog');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    window.scrollTo({ top: 0, behavior: 'auto' });
    loadBlog();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  useEffect(() => {
    let isMounted = true;
    setCommentsLoading(true);

    fetchBlogComments(slug, { page: 1, limit: 20 })
      .then((commentsData) => {
        if (isMounted) {
          setCommentList(commentsData.items);
        }
      })
      .catch(() => {
        if (isMounted) {
          setCommentList([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setCommentsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [slug]);

  useEffect(() => {
    let isMounted = true;
    const requestId = likeStatusRequestIdRef.current + 1;
    likeStatusRequestIdRef.current = requestId;
    const mutationIdAtRequestStart = likeMutationIdRef.current;

    if (!user) {
      setHasLiked(false);
      return undefined;
    }

    fetchMyLikeStatus(slug)
      .then((likeStatus) => {
        if (
          isMounted &&
          requestId === likeStatusRequestIdRef.current &&
          mutationIdAtRequestStart === likeMutationIdRef.current
        ) {
          setHasLiked(likeStatus.liked);
        }
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [slug, user]);

  useEffect(() => {
    if (!blog?.slug) {
      return undefined;
    }

    let cancelled = false;
    const runTracking = () => {
      trackBlogView(blog.slug)
        .then((data) => {
          if (!cancelled && data?.metrics) {
            applyMetrics({
              viewCount: data.metrics.viewCount
            });
          }
        })
        .catch(() => {});
    };

    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(runTracking);
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }

    const timeoutId = window.setTimeout(runTracking, 150);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [blog?.slug]);

  useEffect(() => {
    if (!blog) {
      return;
    }

    const description = String(blog.excerpt || blog.content || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 160);

    applySeo({
      title: `${blog.title} | Race Next Step`,
      description: description || 'Read this blog on Race Next Step.',
      canonicalUrl: `/blogs/${blog.slug}`,
      ogType: 'article',
      ogImage: blog.image || '/favicon.png'
    });
  }, [blog]);

  if (notFound) {
    return <Navigate to="/blogs" replace />;
  }

  if (loading) {
    return <BlogDetailSkeleton />;
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-16 text-center text-sm font-medium text-rose-600">
        {error || 'Unable to load this blog right now.'}
      </div>
    );
  }

  const redirectToSignIn = () => {
    navigate('/signin', { state: { from: { pathname: location.pathname } } });
  };

  const handleLikeClick = async () => {
    if (!user) {
      redirectToSignIn();
      return;
    }

    if (likeLoading) {
      return;
    }

    const nextLikedState = !hasLiked;
    const likeDelta = nextLikedState ? 1 : -1;
    likeMutationIdRef.current += 1;

    setLikeLoading(true);
    setHasLiked(nextLikedState);
    setMetrics((current) => ({
      ...current,
      likeCount: sanitizeMetricValue((current.likeCount || 0) + likeDelta)
    }));

    try {
      const data = await toggleBlogLike(blog.slug);
      setHasLiked(Boolean(data?.liked));
      if (data?.metrics) {
        applyMetrics(data.metrics);
      }
      refreshMetricsFromServer();
      setEngagementMessage(data?.liked ? 'You liked this blog.' : 'Like removed from this blog.');
    } catch (toggleError) {
      setHasLiked(!nextLikedState);
      setMetrics((current) => ({
        ...current,
        likeCount: sanitizeMetricValue((current.likeCount || 0) - likeDelta)
      }));
      setEngagementMessage(toggleError.message || 'Unable to update like right now.');
    } finally {
      setLikeLoading(false);
    }
  };

  const handleCommentSubmit = async (event) => {
    event.preventDefault();

    if (!user) {
      redirectToSignIn();
      return;
    }

    const trimmedComment = commentText.trim();
    if (!trimmedComment || commentSubmitting) {
      return;
    }

    const optimisticComment = {
      id: `temp-${Date.now()}`,
      author: user.displayName || user.email || 'You',
      text: trimmedComment,
      createdAt: new Date().toISOString()
    };

    setCommentSubmitting(true);
    setCommentList((current) => [optimisticComment, ...current]);
    setMetrics((current) => ({
      ...current,
      commentCount: sanitizeMetricValue((current.commentCount || 0) + 1)
    }));
    setCommentText('');

    try {
      const data = await postBlogComment(blog.slug, trimmedComment);
      if (data?.comment) {
        setCommentList((current) =>
          current.map((comment) => (comment.id === optimisticComment.id ? data.comment : comment))
        );
      }
      if (data?.metrics) {
        applyMetrics(data.metrics);
      }
      refreshMetricsFromServer();
      setEngagementMessage('Your comment has been added.');
    } catch (commentError) {
      setCommentList((current) => current.filter((comment) => comment.id !== optimisticComment.id));
      setMetrics((current) => ({
        ...current,
        commentCount: sanitizeMetricValue((current.commentCount || 0) - 1)
      }));
      setCommentText(trimmedComment);
      setEngagementMessage(commentError.message || 'Unable to post comment right now.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 text-[#1f1730]">
      <BlogNavbar
        title="Blogs Hub"
        subtitle="Read full articles, guides, and student experiences"
      />

      <section className="mx-auto max-w-7xl overflow-hidden px-4 pb-8 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]">
          <div className="min-w-0 overflow-hidden rounded-[30px] border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-4 py-4 sm:px-6 sm:py-5 lg:px-8">
              <Link to="/blogs" className="inline-flex items-center gap-2 text-sm font-semibold text-[#9a51a0] transition-colors hover:text-[#ff7f32]">
                <ArrowLeft className="h-4 w-4" />
                Back to blogs
              </Link>
              <div className="mt-5 flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#9a51a0]">
                <span className="rounded-full bg-[#f4ecff] px-3 py-1.5">{blog.category}</span>
                <span>{currentViewsLabel}</span>
              </div>
              <h1 className="mt-4 text-2xl font-extrabold leading-tight text-[#1f1730] sm:text-3xl lg:text-5xl">
                {blog.title}
              </h1>
              <p className="mt-4 max-w-3xl text-base leading-8 text-[#625470] sm:text-lg">
                {blog.excerpt}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-[#625470]">
                <div className="flex items-center gap-2">
                  <UserRound className="h-4 w-4 text-[#9a51a0]" />
                  <span className="font-semibold text-[#241b37]">{blog.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock3 className="h-4 w-4 text-[#9a51a0]" />
                  <span>{blog.readTime} read</span>
                </div>
                <span>{blog.publishDate}</span>
                <button
                  type="button"
                  onClick={handleLikeClick}
                  disabled={likeLoading}
                  className={`flex items-center gap-2 rounded-full px-3 py-1.5 transition-colors ${
                    hasLiked ? 'bg-[#ffe6f1] text-[#d94a8d]' : 'bg-[#fff4f8] text-[#d94a8d]'
                  } ${likeLoading ? 'cursor-wait opacity-70' : ''}`}
                >
                  <Heart className="h-4 w-4" />
                  <span className="font-semibold">{displayLikeCount}</span>
                </button>
                <div className="flex items-center gap-2 rounded-full bg-[#f4efff] px-3 py-1.5 text-[#8255d8]">
                  <MessageSquare className="h-4 w-4" />
                  <span className="font-semibold">{displayCommentCount}</span>
                </div>
                <div className="flex items-center gap-2 rounded-full bg-[#edf4ff] px-3 py-1.5 text-[#4b79d9]">
                  <Eye className="h-4 w-4" />
                  <span className="font-semibold">{currentViewsLabel}</span>
                </div>
                <ShareInlineButton blog={blog} />
              </div>
              {engagementMessage ? (
                <p className="mt-4 text-sm font-medium text-[#7a52b4]">{engagementMessage}</p>
              ) : null}
            </div>

            <div className="px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
              <div className="overflow-hidden rounded-[26px] border border-gray-100 bg-[#f3effa]">
                <img src={blog.image} alt={blog.title} className="h-[220px] w-full object-cover sm:h-[300px]" referrerPolicy="no-referrer" />
              </div>

              <article
                className="prose prose-lg mt-8 max-w-none overflow-hidden prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-[#211731] prose-h2:mt-12 prose-h2:border-l-4 prose-h2:border-[#d7b3f4] prose-h2:pl-4 prose-h2:text-[1.7rem] prose-h3:mt-8 prose-h3:text-[1.25rem] prose-p:text-[1.04rem] prose-p:leading-8 prose-p:text-[#55486a] prose-p:[text-wrap:pretty] prose-strong:text-[#251a38] prose-strong:font-semibold prose-em:text-[#6c3fb3] prose-li:my-2 prose-li:text-[#5b4e70] prose-ul:space-y-1 prose-ul:marker:text-[#9a51a0] prose-blockquote:border-l-4 prose-blockquote:border-[#d7b3f4] prose-blockquote:pl-4 prose-blockquote:text-[#665676] prose-a:text-[#8f48d6] prose-img:max-w-full"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />

              <div className="mt-10 rounded-[28px] border border-[#ece3f8] bg-[#faf8ff] p-5 sm:p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9a51a0]">Engagement</p>
                    <h2 className="mt-2 text-2xl font-bold text-[#20192d]">Comments & Reactions</h2>
                  </div>
                  {!user ? (
                    <button
                      type="button"
                      onClick={redirectToSignIn}
                      className="inline-flex items-center justify-center rounded-2xl border border-[#e4d8fb] bg-white px-4 py-3 text-sm font-semibold text-[#7a52b4] transition-colors hover:bg-[#f8f3ff]"
                    >
                      Sign in to like or comment
                    </button>
                  ) : null}
                </div>

                <div className="mt-5 grid grid-cols-4 gap-2">
                  <button
                    type="button"
                    onClick={handleLikeClick}
                    disabled={likeLoading}
                    className={`flex items-center justify-center gap-1.5 rounded-xl border px-2 py-2.5 text-xs font-semibold transition-all ${
                      hasLiked
                        ? 'border-pink-200 bg-[#fff1f7] text-[#d94a8d]'
                        : 'border-[#eadffb] bg-white text-[#6b5a82] hover:bg-[#f8f3ff]'
                    } ${likeLoading ? 'cursor-wait opacity-70' : ''}`}
                  >
                    <Heart className="h-3.5 w-3.5 shrink-0" />
                    <span>{displayLikeCount}</span>
                  </button>
                  <div className="flex items-center justify-center gap-1.5 rounded-xl border border-[#eadffb] bg-white px-2 py-2.5 text-xs font-semibold text-[#6b5a82]">
                    <MessageSquare className="h-3.5 w-3.5 shrink-0 text-[#8255d8]" />
                    <span>{displayCommentCount}</span>
                  </div>
                  <div className="flex items-center justify-center gap-1.5 rounded-xl border border-[#eadffb] bg-white px-2 py-2.5 text-xs font-semibold text-[#6b5a82]">
                    <Eye className="h-3.5 w-3.5 shrink-0 text-[#4b79d9]" />
                    <span className="truncate">{currentViewsLabel}</span>
                  </div>
                  <ShareInlineButton blog={blog} fullWidth />
                </div>

                <form onSubmit={handleCommentSubmit} className="mt-5">
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-[#241b37]">Add a Comment</span>
                    <textarea
                      value={commentText}
                      onChange={(event) => setCommentText(event.target.value)}
                      placeholder="Share your thoughts on this blog..."
                      rows={4}
                      disabled={commentSubmitting}
                      className="w-full rounded-[24px] border border-[#e7ddf8] bg-white px-4 py-4 text-sm leading-7 outline-none transition focus:border-[#b86ae6] focus:ring-2 focus:ring-[#d59ff5]/40"
                    />
                  </label>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="submit"
                      disabled={commentSubmitting}
                      className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#7f3df1] to-[#ff7f32] px-5 py-3 text-sm font-semibold text-white shadow-lg transition-transform duration-300 hover:scale-[1.02]"
                    >
                      <Send className="h-4 w-4" />
                      {commentSubmitting ? 'Posting...' : 'Post Comment'}
                    </button>
                  </div>
                </form>

                <div className="mt-6">
                  {commentsLoading ? (
                    <div className="rounded-[24px] border border-dashed border-[#e2d8f6] bg-white px-4 py-6 text-center">
                      <p className="text-sm font-semibold text-[#241b37]">Loading comments...</p>
                    </div>
                  ) : formattedComments.length > 0 ? (
                    <CommentList comments={formattedComments} />
                  ) : (
                    <div className="rounded-[24px] border border-dashed border-[#e2d8f6] bg-white px-4 py-6 text-center">
                      <p className="text-sm font-semibold text-[#241b37]">No user comments yet</p>
                      <p className="mt-1 text-sm text-[#7c6d91]">Be the first one to start the conversation.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <aside className="xl:sticky xl:top-6 xl:self-start">
            <div className="rounded-[30px] border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#9a51a0]">Suggestions</p>
                  <h2 className="mt-2 text-2xl font-bold text-[#20192d]">Related Blogs</h2>
                </div>
                <Link to="/blogs" className="inline-flex items-center gap-2 text-sm font-semibold text-[#a43ddd] transition-colors hover:text-[#ff7f32]">
                  Explore More
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="divide-y divide-[#f0eaf8]">
                {blog.relatedBlogs.map((relatedBlog) => (
                  <BlogCard key={relatedBlog.id} blog={relatedBlog} compact />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <ExperienceBanner />
    </div>
  );
};

export default BlogDetailPage;
